/**
 * `/host/:pin` — la partida vista desde el proyector.
 *
 * Está pensada para leerse **desde la última fila**: el código en cifras enormes, el enunciado a
 * 44 px y las opciones a 32 (§15). Es la única pantalla del producto donde aparecen los nombres
 * reales, y aparecen porque el profesor tiene que saber quién es «Tortuga Ninja» cuando pasa algo
 * (§12.3).
 *
 * **Los atajos de teclado no son una comodidad**: el portátil está conectado al proyector y la
 * profesora está de pie, mirando a la clase. Buscar un botón con el ratón mientras cuarenta
 * personas esperan es lo que hace que se deje de usar la herramienta.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import {
  ANSWER_TOKENS,
  isValidPin,
  type HostLeaderboardRow,
  type HostLeaderboardView,
  type HostLobbyView,
  type HostQuestionView,
  type HostRevealView,
  type HostRoomInfo,
  type RoomSettings,
  type SessionProfile,
} from '@clica/shared';
import { useGame, type GameState } from '../game/use-game.js';
import { useClock, useSegundosHasta, type ClockReading } from '../game/use-clock.js';
import type { GameSocket } from '../game/socket.js';
import { EstadoConexion } from '../components/estado-conexion.js';
import { Cronometro } from '../components/cronometro.js';
import { FichaOpcion, letterFor } from '../components/ficha-opcion.js';
import { ContenidoMarkdown } from '../markdown/contenido.js';
import { tamanoSegunTexto } from '../components/escala-opcion.js';
import { Boton } from '../components/sistema/boton.js';
import { DialogoConfirmacion } from '../components/sistema/dialogo-confirmacion.js';
import { Marco } from '../components/sistema/marco.js';
import { useHuecoMedido } from '../components/sistema/avisos.js';
import { AjustesDeSala } from '../components/ajustes-de-sala.js';
import { useErroresComoAvisos } from '../game/usar-errores.js';
import { Glifo } from '../components/sistema/glifo.js';
import { Rotulo } from '../components/sistema/rotulo.js';
import { Clasificacion, Podio } from '../components/clasificacion.js';
import { RepartoRespuestas } from '../components/reparto-respuestas.js';
import { ImagenPregunta } from '../components/imagen-pregunta.js';
import { readSoundEnabled, SoundBoard, writeSoundEnabled } from '../game/sound.js';

/** La marca de la opción correcta. Decorativa: al lado va la palabra que dice lo mismo. */
const CORRECT_MARK = '\u2713';

const CONNECTION_LABELS = {
  online: 'game.state.online',
  offline: 'game.state.offline',
  kicked: 'game.state.kicked',
} as const;

const CONNECTION_TONES: Readonly<Record<string, string>> = {
  online: 'text-ok',
  // Va sobre el fondo de una fila, más claro que el de la página: con `/60` se queda en
  // 4,39:1 y no llega al 4,5:1 que exige AA.
  offline: 'text-text-dim',
  kicked: 'text-ko',
};

export function PantallaSala({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  const { pin = '' } = useParams();
  const { state, socket } = useGame({ pin });
  const clock = useClock(state.anchor);
  const sonido = useSoundBoard(state);

  useHostShortcuts(state.phase, socket);

  // Los errores del protocolo se contaban con un párrafo rojo que nadie limpiaba nunca: se
  // quedaba en pantalla el resto de la sesión, empujando el contenido de la pared.
  useErroresComoAvisos(state.error);

  useEffect(() => {
    document.title = `${pin} · ${t('game.host.title')}`;
  }, [pin, t]);

  if (!isValidPin(pin)) {
    return (
      <main id="contenido" className="mx-auto max-w-md p-6">
        <p role="alert" className="text-ko text-lead font-display font-bold">
          {t('game.join.invalid')}
        </p>
        <Link to="/asignaturas" className="mt-4 inline-block underline underline-offset-4">
          {t('subjects.title')}
        </Link>
      </main>
    );
  }

  const lobby = state.lobby?.view === 'host' ? state.lobby : null;
  const question = state.question?.view === 'host' ? state.question : null;
  const reveal = state.reveal?.view === 'host' ? state.reveal : null;
  const clasificacion = state.leaderboard?.view === 'host' ? state.leaderboard : null;

  /*
   * El título de la pizarra es el nombre del cuestionario.
   *
   * «Sala del profesor» no dice nada de lo que hay proyectado, y con dos clases seguidas en la
   * misma pantalla no hay forma de saber cuál es cuál. El nombre no llegaba al cliente por ningún
   * camino: ha habido que mandarlo. Mientras no llega —el primer instante tras conectar— se usa
   * el nombre genérico, que es preferible a un hueco que salta.
   */
  const titulo = state.room?.quizTitle ?? t('game.host.title');

  return (
    <Marco
      profile={profile}
      migas={[
        { texto: t('subjects.title'), a: '/asignaturas' },
        ...(state.room === null
          ? []
          : [{ texto: titulo, a: `/cuestionarios/${state.room.quizId}` }]),
        { texto: t('game.host.roomCrumb') },
      ]}
    >
      <main
        id="contenido"
        className="mx-auto max-w-5xl p-6 pb-40"
        style={{ minHeight: 'calc(100dvh - 5rem)' }}
      >
        {/*
         * `flex-wrap` y no una sola fila.
         *
         * El título con el PIN a un lado y el sonido con el estado de conexión al otro no caben
         * juntos por debajo de unos 500 px, y sin envolver el resultado no era un recorte: era
         * **desbordamiento horizontal de toda la página**. En un móvil eso ensancha el viewport de
         * maquetación, y con él la barra de mandos fija, que se iba media pantalla a la derecha:
         * «Terminar la partida» dejaba de poder pulsarse sin desplazar la pared en horizontal.
         * Nadie proyecta desde un móvil, pero sí se abre `/host` en el propio teléfono para
         * rescatar una sala cuando el portátil se ha cerrado.
         */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-1">
            <h1 className="font-display text-h3 min-w-0 font-bold text-balance">{titulo}</h1>
            {/* El PIN, en todas las fases y no solo en la sala de espera: quien llega tarde entra
              igual —el servidor no mira la fase para admitir a nadie—, y hasta ahora las cuatro
              cifras desaparecían de la pared en cuanto empezaba la partida. */}
            {state.phase !== 'lobby' && (
              <p className="font-display text-h2 font-extrabold tabular-nums">
                <span className="sr-only">{t('game.host.pinLabel')}: </span>
                {pin}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <InterruptorSonido enabled={sonido.enabled} onToggle={sonido.toggle} />
            <EstadoConexion status={state.status} />
          </div>
        </div>

        {state.phase === 'lobby' ? (
          <SalaDeEspera
            pin={pin}
            lobby={lobby}
            settings={state.room?.settings ?? null}
            socket={socket}
          />
        ) : state.phase === 'countdown' ? (
          <CuentaAtras startsAt={state.countdownStartsAt} />
        ) : state.phase === 'ended' ? (
          <Final
            podium={state.podium}
            leaderboard={clasificacion}
            unsaved={state.unsaved}
            room={state.room}
          />
        ) : question === null ? (
          <p role="status" className="mt-10 text-center">
            {t('common.loading')}
          </p>
        ) : (
          <>
            <Pregunta
              question={question}
              clock={clock}
              frozen={state.frozen}
              hintMd={state.hintMd}
              answered={state.answered}
              total={state.total}
              reveal={reveal}
            />
            {reveal === null ? null : <RepartoRespuestas histogram={reveal.histogram} />}
            {state.phase === 'reveal' && clasificacion !== null ? (
              <Clasificacion leaderboard={clasificacion} />
            ) : null}
            <MandosDelProfesor phase={state.phase} frozen={state.frozen} socket={socket} />
          </>
        )}
      </main>
    </Marco>
  );
}

/**
 * Los atajos del proyector (§15).
 *
 * Son imprescindibles, no una comodidad: el portátil está conectado al proyector y la profesora
 * está de pie, mirando a la clase. Buscar un botón con el ratón mientras cuarenta personas esperan
 * es lo que hace que se deje de usar la herramienta.
 *
 * No se disparan mientras se escribe en un campo, para no cerrar una pregunta al teclear una «c».
 */
function useHostShortcuts(phase: string, socket: GameSocket | null): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const escribiendo =
        target !== null &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable === true);
      if (escribiendo || event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === ' ') {
        event.preventDefault();
        socket?.send(phase === 'reveal' ? { type: 'host:next' } : { type: 'host:start' });
        return;
      }
      if (event.key === 'c' || event.key === 'C') {
        socket?.send({ type: 'host:close_question' });
        return;
      }
      if (event.key === 'p' || event.key === 'P') {
        socket?.send({ type: 'host:pause' });
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [phase, socket]);
}

/**
 * La cuenta atrás proyectada.
 *
 * En la pared, tres segundos de rótulo quieto son tres segundos en los que nadie sabe si la
 * partida va a empezar o si el proyector se ha colgado. La cifra viene del servidor —la marca de
 * arranque que él manda— y no de una constante copiada aquí: el tiempo lo decide él (principio I).
 *
 * `--text-tick` es el token más grande del sistema, 340 px, y llevaba diez fases definido y sin
 * usar. Es exactamente para esto.
 */
function CuentaAtras({ startsAt }: { startsAt: string | null }): React.JSX.Element {
  const { t } = useTranslation();
  const segundos = useSegundosHasta(startsAt);

  return (
    <div className="mt-16 text-center">
      {segundos === null ? null : (
        // La `key` reinicia la animación con cada cifra: sin ella React reutiliza el nodo, la
        // animación no vuelve a arrancar y el número cambia de golpe.
        <p
          key={segundos}
          aria-hidden="true"
          className="cifra font-display text-tick text-edge leading-none font-extrabold tabular-nums"
        >
          {segundos}
        </p>
      )}
      <p role="status" aria-live="assertive" className="font-display text-h1 mt-4 font-extrabold">
        {t('game.countdown.title')}
      </p>
    </div>
  );
}

function Final({
  podium,
  leaderboard,
  unsaved,
  room,
}: {
  podium: readonly HostLeaderboardRow[] | null;
  leaderboard: HostLeaderboardView | null;
  unsaved: number;
  room: HostRoomInfo | null;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="escena mt-10">
      <h2 className="font-display text-h1 text-center font-extrabold">{t('game.ended.title')}</h2>
      {unsaved > 0 && leaderboard !== null ? <SinGuardar leaderboard={leaderboard} /> : null}
      {podium === null || podium.length === 0 ? null : <Podio rows={podium} />}
      {leaderboard === null ? null : <Clasificacion leaderboard={leaderboard} />}

      {/* Salida. Esta pantalla no tenía un solo enlace: al terminar, la única forma de ir a
          cualquier sitio era escribir la dirección a mano. */}
      <div className="mt-10 flex flex-wrap justify-center gap-3">
        {/*
         * **Este enlace llevaba a `/partidas`, que no existe.**
         *
         * Ninguna ruta del enrutador se llama así —hay `/cuestionarios/:quizId/partidas` y
         * `/partidas/:gameId`—, así que al terminar una partida el botón grande y amarillo del
         * proyector caía en la pantalla de «esto no existe», con la clase delante. No era un
         * error de escribir la cadena: el identificador de la partida **no llegaba al cliente**,
         * así que no había nada que poner detrás. Ahora llega en `room:info`.
         */}
        {room === null ? null : (
          <Link
            to={`/partidas/${room.gameId}`}
            className="text-body font-display rounded-chip border-edge bg-hivis text-on-hivis inline-flex min-h-11 items-center border-2 px-5 font-bold"
          >
            {t('game.ended.seeReport')}
          </Link>
        )}
        <Link
          to={room === null ? '/asignaturas' : `/cuestionarios/${room.quizId}`}
          className="text-body font-display rounded-chip border-line inline-flex min-h-11 items-center border-2 px-5 font-bold"
        >
          {t('quizzes.openEditor')}
        </Link>
      </div>
    </div>
  );
}

/**
 * El aviso de que la partida no ha quedado registrada (§13.2).
 *
 * **Es la única oportunidad de conservar el resultado**: si quien aloja la partida cierra la
 * pestaña, lo jugado se pierde. Por eso el aviso es prominente y trae la descarga al lado, no en un
 * menú.
 *
 * El archivo se genera **en el navegador**, con lo que ya está en pantalla: pedírselo al servidor
 * sería pedírselo justo a la parte que no responde.
 */
function SinGuardar({ leaderboard }: { leaderboard: HostLeaderboardView }): React.JSX.Element {
  const { t } = useTranslation();

  const descargar = (): void => {
    const contenido = JSON.stringify(
      {
        clica_format: 1,
        exported_at: new Date().toISOString(),
        leaderboard: leaderboard.rows,
        total: leaderboard.total,
      },
      null,
      2,
    );
    const url = URL.createObjectURL(new Blob([contenido], { type: 'application/json' }));
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = 'clica-partida.json';
    enlace.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div role="alert" className="border-ko mt-8 rounded-xl border-2 p-4">
      <p className="text-ko text-xl font-bold">
        <span aria-hidden="true">{WARN_MARK} </span>
        {t('game.unsaved.title')}
      </p>
      <p className="mt-2">{t('game.unsaved.lead')}</p>
      <Boton variante="primaria" className="mt-4" onClick={descargar}>
        {t('game.unsaved.download')}
      </Boton>
      <p className="text-text-dim mt-2 text-sm">{t('game.unsaved.downloadHelp')}</p>
    </div>
  );
}

const WARN_MARK = '\u26A0';

/**
 * Suena **solo en esta pantalla** (§0.6). Cuarenta móviles pitando a destiempo es ruido, no
 * ambiente, y es lo primero que hace que una clase pida apagarlo todo.
 *
 * Cada efecto acompaña a algo que ya está en pantalla: con el sonido apagado no se pierde nada.
 */
function useSoundBoard(state: GameState): { enabled: boolean; toggle: () => void } {
  const [enabled, setEnabled] = useState(() => readSoundEnabled());
  const boardRef = useRef<SoundBoard | null>(null);
  const previous = useRef({ phase: state.phase, closed: false, seconds: 99 });

  if (boardRef.current === null) boardRef.current = new SoundBoard(enabled);

  useEffect(() => {
    boardRef.current?.setEnabled(enabled);
    writeSoundEnabled(enabled);
  }, [enabled]);

  useEffect(() => {
    const board = boardRef.current;
    if (board === null) return;
    const antes = previous.current;

    if (state.phase === 'countdown' && antes.phase !== 'countdown') board.play('countdown');
    if (state.phase === 'reveal' && antes.phase !== 'reveal') {
      const histogram = state.reveal?.view === 'host' ? state.reveal.histogram : null;
      const aciertos =
        histogram?.bars.filter((bar) => bar.correct).reduce((sum, bar) => sum + bar.count, 0) ?? 0;
      board.play('questionClosed');
      // Ascendente si acertó la mayoría de quienes respondieron, descendente si no (§0.6).
      board.play(aciertos * 2 >= (histogram?.answered ?? 0) ? 'revealUp' : 'revealDown');
      board.play('leaderboard');
    }
    if (state.phase === 'ended' && antes.phase !== 'ended') board.play('podium');

    previous.current = { ...antes, phase: state.phase };
  }, [state.phase, state.reveal]);

  return { enabled, toggle: () => setEnabled((value) => !value) };
}

/** El interruptor. Símbolo y palabra, para que no dependa de reconocer un icono. */
function InterruptorSonido({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={enabled}
      title={t('game.sound.help')}
      className="border-line rounded-chip text-body flex min-h-11 items-center gap-2 border-2 px-3"
    >
      <span aria-hidden="true">{enabled ? SOUND_ON_MARK : SOUND_OFF_MARK}</span>
      <span>{enabled ? t('game.sound.on') : t('game.sound.off')}</span>
    </button>
  );
}

const SOUND_ON_MARK = '\u266A';
const SOUND_OFF_MARK = '\u2205';

/**
 * La pregunta en el proyector.
 *
 * **Las opciones se muestran siempre**, también barajadas: el orden es único para toda la partida,
 * así que «la B» significa lo mismo en el proyector y en los cuarenta móviles. La regla anterior
 * —ocultarlas al barajar— venía de un diseño con un orden por alumno que este producto no hace.
 */
function Pregunta({
  question,
  clock,
  frozen,
  hintMd,
  answered,
  total,
  reveal,
}: {
  question: HostQuestionView;
  clock: ClockReading;
  frozen: 'paused' | 'hostAway' | null;
  hintMd: string | null;
  answered: number;
  total: number;
  reveal: HostRevealView | null;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <section className="mt-6">
      {/* Envuelve: la cabecera de la pregunta —número, cronómetro y recuento— no cabe en una fila
          por debajo de unos 360 px, y sin envolver eso desborda la página entera. */}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <p className="text-text-dim text-lead">
          {t('game.question.of', { index: question.index + 1, total: question.total })}
        </p>
        <Cronometro reading={clock} frozen={frozen} size={110} />
        <p aria-live="polite" className="text-lead tabular-nums">
          {t('game.question.answeredCount', { answered, total })}
        </p>
      </div>

      {question.image === null ? null : (
        <div className="mt-6 flex justify-center">
          <ImagenPregunta
            path={question.image.path}
            alt={question.image.alt}
            width={question.image.width}
            height={question.image.height}
          />
        </div>
      )}

      {/* 44 px mínimo para el enunciado proyectado (§15): tiene que leerse desde la última fila. */}
      {/* `assertive`: la apertura de una pregunta interrumpe a propósito. */}
      <div aria-live="assertive">
        <ContenidoMarkdown source={question.promptMd} className="prose-clica text-stem-proj mt-8" />
      </div>

      {hintMd !== null ? (
        <div
          role="status"
          className="border-edge bg-surface rounded-card mt-6 border-l-8 px-4 py-3"
        >
          <p className="text-text text-sm font-semibold uppercase">{t('game.question.hint')}</p>
          <ContenidoMarkdown source={hintMd} className="prose-clica text-option-proj" />
        </div>
      ) : null}

      {/* Dos filas iguales y con tope: con una opción larga, la segunda fila crecía y se salía
          por debajo de los mandos, que están fijos al pie. Lo que se adapta es la letra. */}
      <ul className="mt-8 grid gap-3 sm:grid-cols-2 sm:grid-rows-2">
        {question.options.map((option, index) => {
          const token = ANSWER_TOKENS[index];
          const esCorrecta = reveal?.correctOptionId === option.id;
          const revelada = reveal !== null;

          return (
            <li
              key={option.id}
              /*
               * Al revelar, las incorrectas **pierden el fondo de color**; no se atenúan con
               * opacidad. Bajar la opacidad del elemento arrastra también el texto: medido, dejaba
               * las letras a 1,78:1 sobre su propio fondo, muy por debajo del 3:1 que exige AA para
               * texto grande. Quitando el fondo, el texto queda sobre el de la página a 18:1 y la
               * correcta destaca por contraste de verdad, no por que las demás se vean peor.
               *
               * La ficha —forma, letra y color— se queda en las cuatro, así que ninguna pierde su
               * identidad y se sigue pudiendo hablar de «la B».
               */
              className={[
                'text-option-proj flex items-center gap-4 rounded-xl px-4 py-3',
                // Filete de tinta siempre: contra el papel la ficha D da 2,12:1 y no llega al 3:1
                // que exige WCAG 1.4.11 al límite de un componente. Ver botones-respuesta.tsx.
                'border-edge border-2',
                revelada && esCorrecta ? 'border-ok border-[6px]' : '',
                revelada && !esCorrecta ? 'border-line' : '',
              ].join(' ')}
              style={
                revelada && !esCorrecta
                  ? undefined
                  : // Una sola tinta para las cuatro: ver el porqué en botones-respuesta.tsx.
                    { backgroundColor: token?.color, color: 'var(--color-on-answer)' }
              }
            >
              {/* La ficha se recorta en el color del papel: sobre el relleno de su propia opción,
                  la forma era invisible y solo quedaba la letra. */}
              <FichaOpcion position={index} size={56} sobreSuColor={!revelada || esCorrecta} />
              {/* El texto se encoge por tramos; el recuadro no se mueve. */}
              <span
                className="min-w-0 flex-1 leading-snug"
                style={{ fontSize: tamanoSegunTexto(option.textMd, '--text-option-proj') }}
              >
                <ContenidoMarkdown source={option.textMd} enLinea />
              </span>
              {/* La correcta se marca con símbolo y con palabra, no solo con el anillo. */}
              {revelada && esCorrecta ? (
                <span className="text-2xl font-bold">
                  <span aria-hidden="true">{CORRECT_MARK}</span>
                  <span className="sr-only">
                    {t('game.reveal.wasCorrect', { letter: letterFor(index) })}
                  </span>
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Los mandos, con el atajo escrito al lado: no sirve de nada un atajo que nadie conoce. */
/**
 * Los mandos, fijos al pie.
 *
 * Antes se apilaban en flujo normal y en la revelación quedaban por debajo del corte: había que
 * desplazar el proyector, delante de la clase, para poder avanzar. Es de las cosas que hacen que
 * una herramienta se deje de usar.
 */
function MandosDelProfesor({
  phase,
  frozen,
  socket,
}: {
  phase: string;
  frozen: 'paused' | 'hostAway' | null;
  socket: GameSocket | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const pausada = frozen === 'paused';
  const [porTerminar, setPorTerminar] = useState(false);
  /*
   * La barra publica su propio alto, y los avisos se colocan encima.
   *
   * Medido y no adivinado: mide 112 px en un portátil y 142 en un teléfono, porque los botones
   * envuelven. Con una constante el número acierta en un ancho y falla en el otro, y lo que falla
   * es que un aviso tape «Terminar la partida» durante ocho segundos con la clase delante.
   */
  const barra = useHuecoMedido<HTMLDivElement>();

  return (
    /*
     * La barra mide lo que mide, y por debajo de 768 px medía dos renglones porque la línea de
     * atajos ocupaba uno entero ella sola. Con ella fuera —es informativa, no un mando— y el
     * `flex-wrap` haciendo su trabajo, la barra vuelve a una fila en un teléfono.
     */
    <div ref={barra} className="border-edge bg-ground fixed inset-x-0 bottom-0 z-30 border-t-2">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 p-4">
        {phase === 'reveal' ? (
          <Boton variante="primaria" onClick={() => socket?.send({ type: 'host:next' })}>
            <Glifo nombre="reanudar" size={20} />
            {t('game.controls.next')}
          </Boton>
        ) : (
          <>
            <Boton
              variante="secundaria"
              onClick={() => socket?.send({ type: pausada ? 'host:resume' : 'host:pause' })}
            >
              <Glifo nombre={pausada ? 'reanudar' : 'pausa'} size={18} />
              {pausada ? t('game.controls.resume') : t('game.controls.pause')}
            </Boton>
            <Boton
              variante="secundaria"
              onClick={() => socket?.send({ type: 'host:close_question' })}
            >
              {t('game.controls.closeQuestion')}
            </Boton>
          </>
        )}

        <Boton variante="destructiva" className="ml-auto" onClick={() => setPorTerminar(true)}>
          {t('game.controls.end')}
        </Boton>
      </div>

      {/* Terminar no tiene vuelta atrás —no hay transición de salida de «terminada»— y hasta
          ahora era un clic directo, mientras borrar una pregunta sí preguntaba. */}
      <DialogoConfirmacion
        abierto={porTerminar}
        destructivo
        titulo={t('game.controls.end')}
        cuerpo={t('game.controls.endConfirm')}
        confirmar={t('game.controls.end')}
        onConfirmar={() => {
          setPorTerminar(false);
          socket?.send({ type: 'host:end' });
        }}
        onCancelar={() => setPorTerminar(false)}
      />
    </div>
  );
}

function SalaDeEspera({
  pin,
  lobby,
  settings,
  socket,
}: {
  pin: string;
  lobby: HostLobbyView | null;
  settings: RoomSettings | null;
  socket: GameSocket | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [porCerrar, setPorCerrar] = useState(false);
  // El origen desnudo: es lo que hay que teclear, y sale del propio navegador para que valga
  // igual en el aula, en una vista previa y en el portátil de casa.
  const donde = window.location.host;

  return (
    <>
      <section aria-labelledby="pin-titulo" className="mt-8 text-center">
        <h2 id="pin-titulo">
          <Rotulo>{t('game.host.pinLabel')}</Rotulo>
        </h2>
        <p className="text-pin-proj font-display -me-[0.18em] leading-none font-extrabold tracking-[0.18em] tabular-nums">
          {pin}
        </p>
        {/* Dónde teclearlo. En toda la pantalla no aparecía la dirección: quien entra por primera
            vez tiene cuatro cifras y ningún sitio donde ponerlas. */}
        <p className="font-display text-h2 mt-4 font-bold">{donde}</p>
        <p className="text-lead text-text-dim mt-2">{t('game.host.pinHint')}</p>
      </section>

      {lobby === null ? null : (
        <>
          <div className="border-line mt-10 flex flex-wrap items-center justify-between gap-4 border-t-2 pt-6">
            <p aria-live="polite" className="text-lead flex items-center gap-2">
              <Glifo nombre="lleno" size={14} className="text-ok" />
              {t('game.lobby.connected', { count: lobby.connectedCount })}
            </p>
          </div>

          {/* Los ajustes se cambian aquí, con la clase delante: es cuando se sabe quién ha
              entrado y qué hace falta. */}
          {settings === null ? null : (
            <AjustesDeSala
              settings={settings}
              onChange={(nuevos) => socket?.send({ type: 'host:settings', settings: nuevos })}
            />
          )}

          <section aria-labelledby="participantes-titulo" className="mt-8">
            <h2 id="participantes-titulo" className="text-xl font-semibold">
              {t('game.host.participants')}
            </h2>

            {lobby.participants.length === 0 ? (
              <p className="text-text-dim mt-4">{t('game.host.empty')}</p>
            ) : (
              // `md` además de `sm` y `lg`: sin él, de 768 a 1023 px quedaban dos columnas muy
              // anchas con el nombre nadando en el centro.
              <ul className="mt-4 grid gap-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                {lobby.participants.map((participant) => (
                  <li
                    key={participant.participantId}
                    // `.entra` sin retraso: las filas se montan de una en una según va entrando
                    // la gente, así que el escalonado lo pone la realidad. Nada acusaba que
                    // alguien entrara en la sala, ni aquí ni en el móvil.
                    className="entra rounded-chip border-line bg-surface flex min-w-0 items-center justify-between gap-3 border-2 px-3 py-2"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      {/* Conexión por forma y por color, nunca solo por color: el círculo lleno
                          es quien está, el vacío quien se ha ido. */}
                      <Glifo
                        nombre={participant.connection === 'online' ? 'lleno' : 'vacio'}
                        size={14}
                        className={CONNECTION_TONES[participant.connection] ?? ''}
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">
                          {participant.displayName}
                        </span>
                        {participant.alias === null ? null : (
                          <span className="text-text-dim block truncate text-sm">
                            {participant.alias}
                          </span>
                        )}
                      </span>
                      <span className="sr-only">
                        {t(CONNECTION_LABELS[participant.connection])}
                      </span>
                    </span>

                    <button
                      type="button"
                      onClick={() =>
                        socket?.send({
                          type: 'host:kick',
                          participantId: participant.participantId,
                        })
                      }
                      className="border-line rounded-chip flex min-h-11 min-w-11 shrink-0 items-center justify-center border-2"
                    >
                      <Glifo nombre="cerrar" size={18} />
                      <span className="sr-only">
                        {t('game.host.kick', { name: participant.displayName })}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="mt-10">
            {/* Por `Boton` y no a mano: el relleno hi-vis da 1,16:1 contra el papel, así que sin
                el filete de tinta el botón más importante de la pantalla no tiene límite ninguno.
                La variante ya lo trae, y además la transición que ahora sí funciona. */}
            <Boton
              variante="primaria"
              disabled={lobby.connectedCount === 0}
              onClick={() => socket?.send({ type: 'host:start' })}
              className="text-lead px-6 py-3"
              {...(lobby.connectedCount === 0 ? { 'aria-describedby': 'empezar-ayuda' } : {})}
            >
              {t('game.host.start')}
            </Boton>
            {lobby.connectedCount === 0 ? (
              <p id="empezar-ayuda" className="text-text-dim text-micro mt-2">
                {t('game.host.startDisabled')}
              </p>
            ) : null}
            {/* Los atajos se leen ANTES de empezar, no con la clase respondiendo. Estaban en la
                barra fija de mandos y eran lo que la obligaba a medir dos renglones en un
                teléfono, tapando el final de la pantalla. */}
            <Rotulo className="mt-4 block">{t('game.controls.shortcuts')}</Rotulo>

            {/* **Cerrar la sala sin haber empezado.** No se podía: los mandos —incluido
                «terminar»— solo se pintan con la partida en marcha, así que una sala abierta por
                error, o con el cuestionario equivocado, se quedaba ocupando su código hasta que
                caducaba. La única salida era cerrar la pestaña, que además la deja resucitable
                durante horas. */}
            <Boton variante="destructiva" className="mt-8" onClick={() => setPorCerrar(true)}>
              <Glifo nombre="papelera" size={18} />
              {t('game.host.closeRoom')}
            </Boton>

            <DialogoConfirmacion
              abierto={porCerrar}
              destructivo
              titulo={t('game.host.closeRoom')}
              cuerpo={t('game.host.closeRoomConfirm')}
              confirmar={t('game.host.closeRoom')}
              onConfirmar={() => {
                setPorCerrar(false);
                socket?.send({ type: 'host:end' });
              }}
              onCancelar={() => setPorCerrar(false)}
            />
          </div>
        </>
      )}
    </>
  );
}
